/**
 * Standardized Error Handling Service
 * Provides centralized error handling, classification, and user-friendly messages
 */

export const ErrorType = {
  NETWORK: 'NETWORK',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  VALIDATION: 'VALIDATION',
  NOT_FOUND: 'NOT_FOUND',
  SERVER_ERROR: 'SERVER_ERROR',
  RATE_LIMIT: 'RATE_LIMIT',
  UNKNOWN: 'UNKNOWN',
} as const;

export type ErrorType = typeof ErrorType[keyof typeof ErrorType];

export const ErrorSeverity = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
} as const;

export type ErrorSeverity = typeof ErrorSeverity[keyof typeof ErrorSeverity];

export interface AppError {
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  userMessage: string;
  code?: string | number;
  originalError?: Error | unknown;
  context?: Record<string, unknown>;
  timestamp: number;
}

export interface ErrorClassification {
  type: ErrorType;
  severity: ErrorSeverity;
  userMessage: string;
  shouldRetry: boolean;
  shouldLogout: boolean;
  shouldRedirect: boolean;
  redirectPath?: string;
}

class ErrorService {
  private static instance: ErrorService;
  private errorCallbacks: Map<ErrorType, ((error: AppError) => void)[]> = new Map();

  static getInstance(): ErrorService {
    if (!ErrorService.instance) {
      ErrorService.instance = new ErrorService();
    }
    return ErrorService.instance;
  }

  /**
   * Classify and standardize error
   */
  classifyError(error: unknown, context?: Record<string, unknown>): AppError {
    const timestamp = Date.now();

    // Handle network errors
    if (this.isNetworkError(error)) {
      return {
        type: ErrorType.NETWORK,
        severity: ErrorSeverity.MEDIUM,
        message: this.extractErrorMessage(error),
        userMessage: 'Lỗi kết nối mạng. Vui lòng kiểm tra kết nối của bạn và thử lại.',
        code: 'NETWORK_ERROR',
        originalError: error,
        context,
        timestamp,
      };
    }

    // Handle axios/http errors
    if (this.isHttpError(error)) {
      return this.handleHttpError(error, context, timestamp);
    }

    // Handle standard JavaScript errors
    if (error instanceof Error) {
      return {
        type: ErrorType.UNKNOWN,
        severity: ErrorSeverity.MEDIUM,
        message: error.message,
        userMessage: 'Đã xảy ra lỗi không mong muốn. Vui lòng thử lại.',
        code: 'JS_ERROR',
        originalError: error,
        context,
        timestamp,
      };
    }

    // Handle string errors
    if (typeof error === 'string') {
      return {
        type: ErrorType.UNKNOWN,
        severity: ErrorSeverity.LOW,
        message: error,
        userMessage: error || 'Đã xảy ra lỗi. Vui lòng thử lại.',
        code: 'STRING_ERROR',
        originalError: error,
        context,
        timestamp,
      };
    }

    // Handle unknown error types
    return {
      type: ErrorType.UNKNOWN,
      severity: ErrorSeverity.MEDIUM,
      message: 'Unknown error occurred',
      userMessage: 'Đã xảy ra lỗi không xác định. Vui lòng tải lại trang.',
      code: 'UNKNOWN_ERROR',
      originalError: error,
      context,
      timestamp,
    };
  }

  /**
   * Check if error is a network error
   */
  private isNetworkError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;

    const err = error as any;
    return (
      err.code === 'ECONNABORTED' ||
      err.code === 'ENOTFOUND' ||
      err.code === 'ECONNREFUSED' ||
      err.message?.includes('Network Error') ||
      err.message?.includes('fetch')
    );
  }

  /**
   * Check if error is an HTTP error
   */
  private isHttpError(error: unknown): error is { response: unknown; status: number } {
    return !!(
      error &&
      typeof error === 'object' &&
      'response' in error &&
      'status' in error
    );
  }

  /**
   * Handle HTTP errors with specific status codes
   */
  private handleHttpError(
    error: unknown,
    context: Record<string, unknown> | undefined,
    timestamp: number
  ): AppError {
    const httpError = error as any;
    const status = httpError.response?.status;
    const responseMessage = httpError.response?.data?.message;
    const errorCode = httpError.response?.data?.code;

    switch (status) {
      case 400:
        return {
          type: ErrorType.VALIDATION,
          severity: ErrorSeverity.LOW,
          message: responseMessage || 'Bad Request',
          userMessage: responseMessage || 'Dữ liệu không hợp lệ. Vui lòng kiểm tra lại.',
          code: errorCode || 400,
          originalError: error,
          context,
          timestamp,
        };

      case 401:
        return {
          type: ErrorType.AUTHENTICATION,
          severity: ErrorSeverity.HIGH,
          message: responseMessage || 'Unauthorized',
          userMessage: 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.',
          code: errorCode || 401,
          originalError: error,
          context,
          timestamp,
        };

      case 403:
        return {
          type: ErrorType.AUTHORIZATION,
          severity: ErrorSeverity.MEDIUM,
          message: responseMessage || 'Forbidden',
          userMessage: 'Bạn không có quyền thực hiện hành động này.',
          code: errorCode || 403,
          originalError: error,
          context,
          timestamp,
        };

      case 404:
        return {
          type: ErrorType.NOT_FOUND,
          severity: ErrorSeverity.LOW,
          message: responseMessage || 'Not Found',
          userMessage: 'Không tìm thấy dữ liệu yêu cầu.',
          code: errorCode || 404,
          originalError: error,
          context,
          timestamp,
        };

      case 419:
        return {
          type: ErrorType.AUTHENTICATION,
          severity: ErrorSeverity.HIGH,
          message: responseMessage || 'Token Expired',
          userMessage: 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.',
          code: errorCode || 419,
          originalError: error,
          context,
          timestamp,
        };

      case 429:
        return {
          type: ErrorType.RATE_LIMIT,
          severity: ErrorSeverity.MEDIUM,
          message: responseMessage || 'Too Many Requests',
          userMessage: 'Quá nhiều yêu cầu. Vui lòng thử lại sau.',
          code: errorCode || 429,
          originalError: error,
          context,
          timestamp,
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          type: ErrorType.SERVER_ERROR,
          severity: ErrorSeverity.HIGH,
          message: responseMessage || 'Server Error',
          userMessage: 'Lỗi máy chủ. Vui lòng thử lại sau.',
          code: errorCode || status,
          originalError: error,
          context,
          timestamp,
        };

      default:
        return {
          type: ErrorType.UNKNOWN,
          severity: ErrorSeverity.MEDIUM,
          message: responseMessage || 'HTTP Error',
          userMessage: 'Đã xảy ra lỗi. Vui lòng thử lại.',
          code: errorCode || status,
          originalError: error,
          context,
          timestamp,
        };
    }
  }

  /**
   * Extract error message from various error types
   */
  private extractErrorMessage(error: unknown): string {
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message;
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as any).message);
    }
    return 'Unknown error occurred';
  }

  /**
   * Get error classification for handling
   */
  getClassification(error: AppError): ErrorClassification {
    const baseClassification = {
      type: error.type,
      severity: error.severity,
      userMessage: error.userMessage,
      shouldRetry: false,
      shouldLogout: false,
      shouldRedirect: false,
    };

    switch (error.type) {
      case ErrorType.NETWORK:
        return {
          ...baseClassification,
          shouldRetry: true,
        };

      case ErrorType.AUTHENTICATION:
        return {
          ...baseClassification,
          shouldLogout: true,
          shouldRedirect: true,
          redirectPath: '/auth/login',
        };

      case ErrorType.AUTHORIZATION:
        return {
          ...baseClassification,
          shouldRedirect: true,
        };

      case ErrorType.RATE_LIMIT:
        return {
          ...baseClassification,
          shouldRetry: true,
        };

      case ErrorType.SERVER_ERROR:
        return {
          ...baseClassification,
          shouldRetry: true,
        };

      default:
        return baseClassification;
    }
  }

  /**
   * Register error callback for specific error type
   */
  onError(type: ErrorType, callback: (error: AppError) => void): () => void {
    if (!this.errorCallbacks.has(type)) {
      this.errorCallbacks.set(type, []);
    }

    this.errorCallbacks.get(type)!.push(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.errorCallbacks.get(type);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  /**
   * Trigger error callbacks
   */
  private triggerCallbacks(error: AppError): void {
    const callbacks = this.errorCallbacks.get(error.type);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(error);
        } catch (callbackError) {
          console.error('Error in error callback:', callbackError);
        }
      });
    }
  }

  /**
   * Handle error with classification and callbacks
   */
  handleError(error: unknown, context?: Record<string, unknown>): ErrorClassification {
    const appError = this.classifyError(error, context);
    const classification = this.getClassification(appError);

    // Log error
    if (import.meta.env.DEV) {
      console.error('Handled error:', appError);
    } else {
      // In production, send to logging service
      this.logError(appError);
    }

    // Trigger callbacks
    this.triggerCallbacks(appError);

    return classification;
  }

  /**
   * Log error to external service
   */
  private logError(error: AppError): void {
    try {
      const logData = {
        type: error.type,
        severity: error.severity,
        message: error.message,
        code: error.code,
        context: error.context,
        timestamp: error.timestamp,
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      // Send to logging service
      console.error('Production error log:', logData);
    } catch (loggingError) {
      console.error('Failed to log error:', loggingError);
    }
  }

  /**
   * Create user-friendly error message
   */
  createUserMessage(error: unknown, defaultMessage = 'Đã xảy ra lỗi. Vui lòng thử lại.'): string {
    const appError = this.classifyError(error);
    return appError.userMessage || defaultMessage;
  }
}

// Export singleton instance
export const errorService = ErrorService.getInstance();

// Export convenience functions
export const handleError = (error: unknown, context?: Record<string, unknown>) => {
  return errorService.handleError(error, context);
};

export const createUserMessage = (error: unknown, defaultMessage?: string) => {
  return errorService.createUserMessage(error, defaultMessage);
};